/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CallFlowSettingsSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/


import { Settings } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Label } from '../../../components/ui/label';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import RecordingSelect from '../../../components/ui/RecordingSelect';
import type { CallFlowSettings } from '../types';

interface CallFlowSettingsSectionProps {
    settings: CallFlowSettings;
    onSettingChange: <K extends keyof CallFlowSettings>(field: K, value: CallFlowSettings[K]) => void;
    t: (key: string) => string;
}

export function CallFlowSettingsSection({ settings, onSettingChange, t }: CallFlowSettingsSectionProps) {
    return (
        <FormBox title={t('callFlows.sections.settings')} icon={<Settings className="h-5 w-5" />}>
            <div className="grid items-center gap-4 sm:grid-cols-2 lg:grid-cols-4">

                {/* Announcement Enabled */}
                <Label htmlFor="announcementEnabled">
                    <SimpleTooltip content={t('callFlows.settings.announcementEnabled.tooltip')}>
                        {t('callFlows.settings.announcementEnabled.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <RecordingSelect
                        value={settings.announcement_enabled ?? ''}
                        onChange={(v) => onSettingChange('announcement_enabled', v || '')}
                        placeholder={t('callFlows.settings.announcementEnabled.placeholder')}
                        returnType="file_path"
                        showPlayButton
                    />
                </div>

                {/* Announcement Disabled */}
                <Label htmlFor="announcementDisabled">
                    <SimpleTooltip content={t('callFlows.settings.announcementDisabled.tooltip')}>
                        {t('callFlows.settings.announcementDisabled.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <RecordingSelect
                        value={settings.announcement_disabled ?? ''}
                        onChange={(v) => onSettingChange('announcement_disabled', v || '')}
                        placeholder={t('callFlows.settings.announcementDisabled.placeholder')}
                        returnType="file_path"
                        showPlayButton
                    />
                </div>

                {/* Play Announcement */}
                <Label htmlFor="playAnnouncement">
                    <SimpleTooltip content={t('callFlows.settings.playAnnouncement.tooltip')}>
                        {t('callFlows.settings.playAnnouncement.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle
                        value={settings.play_announcement}
                        onChange={(v) => onSettingChange('play_announcement', v)}
                    />
                </div>

                {/* Record Call */}
                <Label htmlFor="recordCall">
                    <SimpleTooltip content={t('callFlows.settings.recordCall.tooltip')}>
                        {t('callFlows.settings.recordCall.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle
                        value={settings.record_call}
                        onChange={(v) => onSettingChange('record_call', v)}
                    />
                </div>

                {/* BLF Behavior */}
                <Label htmlFor="blfBehavior">
                    <SimpleTooltip content={t('callFlows.settings.blfBehavior.tooltip')}>
                        {t('callFlows.settings.blfBehavior.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle
                        value={settings.blf_behavior === 'sync'}
                        onChange={(v) => onSettingChange('blf_behavior', v ? 'sync' : 'none')}
                    />
                </div>

            </div>
        </FormBox>
    );
}
